/* Copyright (c) 2020-present CloudIO Inc */

import {
  DataRecord,
  GenericDataRecord,
  MULTIPLE_SELECT_OPS,
  OPS_ICONS,
  SELECT_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import {
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  BaseProps,
  Form,
  isNotEmpty,
  SearchSelectField,
  SelectReturnValueMapping,
} from 'componentregistry';
import {
  useCurrentDataStore,
  useStoreRecords,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { getAttributeList } from 'components/Select/Select';
import { parseOptionValues } from 'components/Select/SelectUtils';
import {
  allSelectOps,
  hasSelectEditor,
} from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import {
  Option,
  SelectInputProps,
  VirtualizedMultiSelectInput,
  VirtualizedSelectInput,
} from 'custom/SelectInput';
import { useFocus } from 'hooks/useFocus';
import Icon from 'icons/Icon';
import React, { memo, useEffect, useId, useMemo, useState } from 'react';
import { useVisibleChildItems } from 'registry/Components';
import { useStoreAttributeFilter, useStoreBusy } from 'store/useAppStore';
import WaveDots from 'utils/WaveDots';
import { useTranslate } from '../../utils/lang';
import { useStringExpression } from 'expression/useExpression';
import useTrigger from 'triggers/useTrigger';
import zIndex from 'constants/ZIndex';

interface SelectProps
  extends Required<SearchSelectField.SearchSelectField>,
    BaseProps<'SearchSelectField'> {}

interface Props extends SelectProps, Form.HorizontalFormProps {
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

function IOSearchSelectField(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    className,
    comboDatasourceAlias,
    defaultOperator,
    doNotPrefetch,
    domProps,
    flex,
    formHorizontal,
    hideOperatorChooser,
    itemId,
    labelAlignment,
    labelWidth,
    optionValues,
    pageId,
    platform,
    required,
    viewAttribute,
    headerFilter,
    roleUid,
    onHeaderFilterSearch,
  } = props;

  let { helperText, placeholder } = props;
  const { label } = props;
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  labelResult = t(labelResult);
  placeholder = t(placeholder);

  let { labelAttribute, valueAttribute } = props;
  const selectStore = comboDatasourceAlias
    ? platform.getStore<GenericDataRecord>(appUid, pageId, comboDatasourceAlias)
    : null;

  const variant = useFieldVariant(appUid);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording()) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<SELECT_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState('');
  const [multiValue, setMultiValue] = useState<string[]>([]);
  const opOptions = getOpOptions('Select', t);

  const store = useCurrentDataStore<DataRecord>();
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);

  useEffect(() => {
    if (filter) {
      const op = getOperator(filter) as SELECT_OPS_KEY_TYPE;
      setOps(op);
      const val = getValue(filter) as string | string[];
      if (Array.isArray(val)) {
        setMultiValue(val);
        setValue('');
      } else {
        setValue(val);
        setMultiValue([]);
      }
    } else {
      // #974 Fix. Any filter other than values has to be set to default operator
      setOps(defaultOperator);
      setValue('');
      setMultiValue([]);
    }
  }, [defaultOperator, filter, setValue]);

  const hasEditor = hasSelectEditor(ops);

  function updateFilter(
    attr: string,
    op: SELECT_OPS_KEY_TYPE,
    val: string,
    multiVal: string[],
  ) {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== attr);
    const _hasEditor = hasSelectEditor(op);
    let multi = false;
    if (op in MULTIPLE_SELECT_OPS) {
      multi = true;
    }
    if (!_hasEditor || (multi ? multiVal.length : isNotEmpty(val))) {
      const v: string | string[] = _hasEditor ? (multi ? multiVal : val) : '';
      const _filter = {
        [attr]: { [op]: v },
      };
      filters.push(_filter);
    } else {
      setOps(op);
      setValue('');
      setMultiValue([]);
    }

    store?.setSmartSeachFilter(filters);
  }

  let options: Option[] = [];
  const records = useStoreRecords(selectStore);
  if (selectStore) {
    options = records;
  } else if (optionValues) {
    const _optionValues = platform.evalIfExpressionString(
      appUid,
      pageId,
      itemId,
      'optionValues',
      {},
      optionValues,
    );
    options = _optionValues ? parseOptionValues(_optionValues) : [];
    valueAttribute = 'value';
    labelAttribute = 'label';
  }

  const mappings = useVisibleChildItems(
    appUid,
    pageId,
    itemId,
  ) as SelectReturnValueMapping.SelectReturnValueMappingItem[];

  const attributeList = useMemo(
    () =>
      getAttributeList(selectStore, mappings, valueAttribute, labelAttribute),
    [labelAttribute, mappings, selectStore, valueAttribute],
  );

  const [onChangeTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnChange',
  );

  useEffect(() => {
    if (
      selectStore &&
      attributeList.length &&
      !doNotPrefetch /* && !store.hasInitialQueryFired() */
    ) {
      if (!selectStore.ignorePKDuplicate) selectStore.ignorePKDuplicate = true;
      selectStore.query({
        props: {
          selectAttributeCodeList: attributeList,
          fetchDistinct: true,
        },
      });
    }
  }, [attributeList, doNotPrefetch, selectStore]);

  if (
    selectStore &&
    !selectStore.hasAutoQueryFired() &&
    attributeList.length &&
    !doNotPrefetch
  ) {
    const s = selectStore;
    platform.quietCallback(() => {
      s.setAutoQueryFired();
      if (!s.ignorePKDuplicate) s.ignorePKDuplicate = true;
      s.query({
        props: {
          selectAttributeCodeList: attributeList,
          fetchDistinct: true,
        },
      });
    });
  }

  const inputRef = useFocus(itemId);
  const isBusy = useStoreBusy(selectStore);

  let multiple = false;
  if (ops in MULTIPLE_SELECT_OPS) {
    multiple = true;
  }
  labelResult =
    ops === 'is' ? labelResult : `${labelResult} (${allSelectOps[ops]})`;

  const id = useId();
  const selectFieldProps: SelectInputProps<Option> = {
    id,
    autoFocus,
    value,
    variant,
    required,
    onChange: async (changedVal: string, option: Option | null) => {
      const newValue = changedVal;
      setValue(newValue);
      updateFilter(viewAttribute, ops, newValue, []);
      if (platform.isRecording() && option && !headerFilter) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', '${option[labelAttribute]}');`,
        );
      }
      if (mappings) {
        mappings
          .filter(
            (mapping) =>
              mapping.props.sourceAttribute && mapping.props.targetAttribute,
          )
          .forEach((mapping) => {
            if (option) {
              const _optionValue = option[mapping.props.sourceAttribute];
              if (_optionValue) {
                updateFilter(
                  mapping.props.targetAttribute,
                  ops,
                  _optionValue as string,
                  [],
                );
              }
            }
          });
      }
      onChangeTrigger?.(undefined, {
        oldValue: value,
        value: changedVal,
      });
    },
    IOInputProps: {
      inputRef,
      startAdornment: (
        <InputAdornment position="start">
          {hideOperatorChooser ? undefined : (
            <IconButton
              data-testid={`${itemId}-op`}
              onClick={handleClick}
              data-tip={allSelectOps[ops] || ops}
            >
              <Icon iconName={OPS_ICONS[ops] || 'equals'} />
            </IconButton>
          )}
          {isBusy ? <WaveDots /> : null}
        </InputAdornment>
      ),
    },
    valueAttribute,
    labelAttribute,
    options,
    placeholder: hasEditor ? placeholder : allSelectOps[ops],
    disabled: !hasEditor,
    sx: { width: headerFilter ? '300px' : undefined },
  };

  const control = multiple ? (
    formHorizontal && !headerFilter ? (
      <VirtualizedMultiSelectInput
        {...selectFieldProps}
        value={multiValue}
        onChange={(changedVal: string[], option: Option[] | null) => {
          setMultiValue(changedVal);
          updateFilter(viewAttribute, ops, '', changedVal);
        }}
      />
    ) : (
      <VirtualizedMultiSelectInput
        {...selectFieldProps}
        label={headerFilter ? undefined : labelResult}
        helpText={helperText}
        value={multiValue}
        onChange={(changedVal: string[], option: Option[] | null) => {
          setMultiValue(changedVal);
          updateFilter(viewAttribute, ops, '', changedVal);
        }}
        limitTags={headerFilter ? 1 : undefined}
      />
    )
  ) : formHorizontal && !headerFilter ? (
    <VirtualizedSelectInput {...selectFieldProps} />
  ) : (
    <VirtualizedSelectInput
      {...selectFieldProps}
      label={headerFilter ? undefined : labelResult}
      helpText={helperText}
    />
  );

  return (
    <>
      <FieldWrapper
        className={className}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal ? (
          <HorizontalFormField
            control={control}
            helperText={helperText}
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : (
          control
        )}
      </FieldWrapper>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        sx={{ zIndex: zIndex.Tooltip + 1 }}
      >
        {opOptions.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as SELECT_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(viewAttribute, op, value, multiValue);
                if (hasSelectEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default memo(React.forwardRef(IOSearchSelectField));
